import * as React from 'react';

import { Typography } from '@mui/material';

import { COUNTRY_NAME_EN, COUNTRY_NAME_NL } from '@kamernet/constants/Common';
import { RoomTypeID } from '@kamernet/core/ApiClient/Kamernet';
import { useIntl } from '@kamernet/core/Intl';
import { Locale } from '@kamernet/core/Intl/types';
import { ROUTES } from '@kamernet/core/Routing';
import { getListingTypeSlugById } from '@kamernet/core/Routing/dynamicRouterMatcher';
import { NavLink } from '@kamernet/core/Routing/NavLink';
import KNLogo from '@kamernet/icons/KN_Logo.svg';
import { useMediaIsDesktop } from '@kamernet/utilities/Media/useMediaIsDesktop';

import { Breadcrumbs as MuiBreadcrumbs } from '@kamernet/components/Breadcrumbs';
import { ListingsContext } from '@kamernet/modules/Listing/context/ListingsContext';
import { RoomTypeIDPluralTranslations } from '@kamernet/modules/Listing/translations';

import styles from './Breadcrumbs.module.css';

const capitalizeFirstLetter = (text: string) => {
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const Breadcrumbs = () => {
  const { T, locale } = useIntl();
  const placeHolder = '...';
  const isDesktop = useMediaIsDesktop();
  const { listingsState } = ListingsContext.useContext();

  const listingTypeId: RoomTypeID | undefined =
    listingsState.filterResult.filters.listingTypeIds &&
    listingsState.filterResult.filters.listingTypeIds.length > 1
      ? undefined
      : listingsState.filterResult.filters.listingTypeIds?.[0] || undefined;

  const cityName =
    listingsState.filterResult.filters.location?.cityName ||
    (locale === Locale.en
      ? capitalizeFirstLetter(COUNTRY_NAME_EN)
      : capitalizeFirstLetter(COUNTRY_NAME_NL));

  const citySlug =
    listingsState.filterResult.filters.location?.citySlug ||
    (locale === Locale.en ? COUNTRY_NAME_EN : COUNTRY_NAME_NL);

  const { streetSlug } = listingsState.filterResult.filters;

  const listingTypeSlug = getListingTypeSlugById(listingTypeId, locale);

  return (
    <section data-testid="breadcrumb-container">
      <MuiBreadcrumbs>
        <MuiBreadcrumbs.Link href={ROUTES.Home.url({}, locale)}>
          <KNLogo className={styles.icon} />
          {T('LBL_SMARTBANNER_TITLE')}
        </MuiBreadcrumbs.Link>
        <MuiBreadcrumbs.Link
          href={ROUTES.ListingSearchCity.url(
            {
              typeAndCity: `${listingTypeSlug}-${citySlug}`,
            },
            locale,
          )}
        >
          {locale === Locale.en
            ? T(
                '%_%',
                cityName,
                listingTypeId === undefined
                  ? T('listings_page.breadcrumbs.listing_type.generic')
                  : T(RoomTypeIDPluralTranslations[listingTypeId]),
              )
            : T(
                '%_%',
                listingTypeId === undefined
                  ? T('listings_page.breadcrumbs.listing_type.generic')
                  : T(RoomTypeIDPluralTranslations[listingTypeId]),
                cityName,
              )}
        </MuiBreadcrumbs.Link>

        {streetSlug && (
          <MuiBreadcrumbs.Text>
            {isDesktop ? (
              <NavLink.Anchor
                href={ROUTES.ListingSearchStreet.url(
                  {
                    typeAndCity: `${listingTypeSlug}-${citySlug}`,
                    street: streetSlug,
                  },
                  locale,
                )}
              >
                {locale === Locale.en
                  ? T(
                      '%_%',
                      listingsState.filterResult.filters.streetName,
                      listingTypeId === undefined
                        ? T('listings_page.breadcrumbs.listing_type.generic')
                        : T(RoomTypeIDPluralTranslations[listingTypeId]),
                    )
                  : T(
                      '%_%',
                      listingTypeId === undefined
                        ? T('listings_page.breadcrumbs.listing_type.generic')
                        : T(RoomTypeIDPluralTranslations[listingTypeId]),
                      listingsState.filterResult.filters.streetName,
                    )}
              </NavLink.Anchor>
            ) : (
              <Typography>{placeHolder}</Typography>
            )}
          </MuiBreadcrumbs.Text>
        )}
      </MuiBreadcrumbs>
    </section>
  );
};
